/**
 * IntegrationQuickActions — akcje integracji ERP na poziomie całej integracji, osadzone
 * w nagłówku szczegółu (`IntegrationDetail`). Przeniesione z rządka ikon listy integracji:
 *   - Test połączenia (modal statusu joba),
 *   - Wygeneruj / Regeneruj token agenta (tylko tryb agentowy; regeneracja za potwierdzeniem).
 *
 * Synchronizacja słowników przeniesiona do zakładki „Słowniki" pulpitu (`DictionarySyncPanel`).
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { KeyRound, Plug } from 'lucide-react';

import { Button } from '@/components/Button';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';

import { AgentTokenModal } from './AgentTokenModal';
import { type ErpIntegrationDto } from './IntegrationsTab';
import { TestConnectionModal } from './TestConnectionModal';

const LIST = 'tenant.settings.integrations.list';

export function IntegrationQuickActions({ integration }: { integration: ErpIntegrationDto }) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['erp-integrations'] });

  const [testJob, setTestJob] = useState<{ jobId: string | null; name: string } | null>(null);
  const [tokenModal, setTokenModal] = useState<{ token: string; name: string } | null>(null);
  const [confirmRegen, setConfirmRegen] = useState(false);

  // Token pokazujemy tylko dla trybu agentowego.
  const isAgentMode = integration.connectionMode === 'agent';

  const testConnectionMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post<{ jobId: string }>(
        `/erp-integrations/${integration.id}/test-connection`,
      );
      return res.data.jobId;
    },
    onMutate: () => setTestJob({ jobId: null, name: integration.name }),
    onSuccess: (jobId) => setTestJob({ jobId, name: integration.name }),
    onError: (err) => {
      setTestJob(null);
      const code = isAxiosError(err) ? (err.response?.data?.error as string | undefined) : null;
      if (code === 'AGENT_TOKEN_NOT_GENERATED') {
        toast.show({
          variant: 'warning',
          title: t('tenant.settings.integrations.toast.tokenNotGenerated'),
        });
        return;
      }
      if (code === 'INTEGRATION_DISABLED') {
        toast.show({
          variant: 'warning',
          title: t('tenant.settings.integrations.toast.integrationDisabled'),
        });
        return;
      }
      toast.show({
        variant: 'error',
        title: t('tenant.settings.integrations.toast.testConnectionError'),
      });
    },
  });

  const regenerateTokenMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post<{ agentToken: string }>(
        `/erp-integrations/${integration.id}/regenerate-agent-token`,
      );
      return res.data.agentToken;
    },
    onSuccess: (token) => {
      invalidate();
      setTokenModal({ token, name: integration.name });
      setConfirmRegen(false);
    },
    onError: () => {
      toast.show({
        variant: 'error',
        title: t('tenant.settings.integrations.toast.tokenRegenerateError'),
      });
      setConfirmRegen(false);
    },
  });

  return (
    <>
      <div className="flex flex-wrap gap-2">
        <Button
          variant="secondary"
          size="sm"
          leftIcon={<Plug className="h-4 w-4" />}
          loading={testConnectionMutation.isPending}
          onClick={() => testConnectionMutation.mutate()}
          data-testid={`integration-test-${integration.id}`}
        >
          {t(`${LIST}.actionTestConnection`)}
        </Button>
        {isAgentMode && (
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<KeyRound className="h-4 w-4" />}
            loading={regenerateTokenMutation.isPending}
            onClick={() => {
              if (integration.agentTokenSet) setConfirmRegen(true);
              else regenerateTokenMutation.mutate();
            }}
            data-testid={`integration-token-${integration.id}`}
          >
            {integration.agentTokenSet
              ? t(`${LIST}.actionRegenerateToken`)
              : t(`${LIST}.actionGenerateToken`)}
          </Button>
        )}
      </div>

      <TestConnectionModal
        open={testJob !== null}
        jobId={testJob?.jobId ?? null}
        integrationName={testJob?.name ?? ''}
        onClose={() => setTestJob(null)}
      />
      <AgentTokenModal
        open={tokenModal !== null}
        token={tokenModal?.token ?? ''}
        integrationName={tokenModal?.name ?? ''}
        onClose={() => setTokenModal(null)}
      />
      <ConfirmDialog
        open={confirmRegen}
        title={t('tenant.settings.integrations.confirmRegenToken.title')}
        message={t('tenant.settings.integrations.confirmRegenToken.message', {
          name: integration.name,
        })}
        confirmLabel={t(`${LIST}.actionRegenerateToken`)}
        variant="danger"
        onConfirm={() => regenerateTokenMutation.mutate()}
        onClose={() => setConfirmRegen(false)}
      />
    </>
  );
}
